import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { ApiCallError, callFunction } from '@/lib/api';
import { xdis16VersionText } from '@/lib/xdis/xdisView';
import { XdisViewer } from './XdisViewer';
import { Xdis21Form } from './Xdis21Form';
import { formatDanishDateTime } from '@/lib/danishDateTime';

export type Xdis21Row = {
  exchangeId: string;
  episodeOfCareIdentifier: string | null;
  letterIdentifier: string | null;
  at: string;
  version: number;
  versions: number;
  updated: boolean;
  receiverName: string | null;
};

type Viewed = { exchangeId: string; kind: string; patientId: string | null; xml: string };

function formatTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : formatDanishDateTime(d);
}

export function Xdis21PlanList({
  clinicId,
  patientId,
  patientName,
  sourceExchangeId,
  canRevealCpr = true,
}: {
  clinicId: string;
  patientId: string;
  patientName: string | null;
  sourceExchangeId?: string | null;
  canRevealCpr?: boolean;
}) {
  const { t } = useTranslation();
  const [viewed, setViewed] = useState<Viewed | null>(null);
  const [viewError, setViewError] = useState<string | null>(null);
  const [formFor, setFormFor] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ['communicate-xdis21', clinicId, patientId],
    staleTime: 30_000,
    queryFn: () =>
      callFunction<Record<string, unknown>, { rows: Xdis21Row[] }>('communicate-xdis', {
        action: 'list21',
        clinic_id: clinicId,
        patient_id: patientId,
      }),
  });

  async function onView(row: Xdis21Row) {
    setViewError(null);
    try {
      setViewed(await callFunction<Record<string, unknown>, Viewed>('communicate-xdis', { action: 'view', clinic_id: clinicId, exchange_id: row.exchangeId }));
    } catch (err) {
      setViewError(err instanceof ApiCallError ? err.message : t('patients.xdis.view_failed', 'Rapporten kunne ikke vises.'));
    }
  }

  const rows = list.data?.rows ?? [];
  if (formFor && sourceExchangeId) {
    return (
      <section className="space-y-3 rounded-md border border-ds-line p-4" data-testid="xdis21-plans">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">{t('patients.xdis.plans_title', 'Patientens plejeforløbsplaner (XDIS21)')}</h3>
          <Button type="button" size="sm" variant="secondary" onClick={() => void list.refetch()} disabled={list.isFetching}>
            {list.isFetching ? t('medcom.correspondence.refreshing', 'Opdaterer…') : t('medcom.correspondence.refresh', 'Opdatér')}
          </Button>
        </div>
        <Xdis21Form
          clinicId={clinicId}
          patientId={patientId}
          patientName={patientName}
          sourceExchangeId={sourceExchangeId}
          carePlanExchangeId={formFor}
          canRevealCpr={canRevealCpr}
          onClose={() => setFormFor(null)}
        />
      </section>
    );
  }
  return (
    <section className="space-y-3 rounded-md border border-ds-line p-4" data-testid="xdis21-plans">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{t('patients.xdis.plans_title', 'Patientens plejeforløbsplaner (XDIS21)')}</h3>
        <Button type="button" size="sm" variant="secondary" onClick={() => void list.refetch()} disabled={list.isFetching}>
          {list.isFetching ? t('medcom.correspondence.refreshing', 'Opdaterer…') : t('medcom.correspondence.refresh', 'Opdatér')}
        </Button>
      </div>
      {list.error && (
        <p role="alert" className="text-sm text-ds-red-ink">
          {list.error instanceof ApiCallError ? list.error.message : t('patients.xdis.plans_failed', 'Plejeforløbsplaner kunne ikke hentes.')}
        </p>
      )}
      {list.isSuccess && rows.length === 0 && <p className="text-sm text-ds-muted">{t('patients.xdis.plans_empty', 'Ingen plejeforløbsplaner.')}</p>}
      <ul className="space-y-2">
        {rows.map((row) => (
          <li key={row.exchangeId} className="space-y-1 rounded-md bg-ds-sunk p-3 text-sm" data-testid="xdis21-plan-row">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="font-medium">{row.receiverName ?? t('patients.xdis.form21_title', 'Plejeforløbsplan (XDIS21)')}</span>
              <span className={row.updated ? 'rounded bg-ds-amber-tint px-1.5 py-0.5 text-xs font-semibold text-ds-amber-ink' : 'text-xs text-ds-muted'} data-testid="xdis21-version">
                {xdis16VersionText(row) || t('patients.xdis.version_one', 'Version 1')}
              </span>
            </div>
            <p className="text-xs text-ds-muted">
              {t('patients.xdis.episode', 'Forløb')}: <span className="font-mono">{row.episodeOfCareIdentifier ?? '–'}</span>
              {` · ${formatTime(row.at)}`}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" variant="secondary" onClick={() => void onView(row)}>
                {t('patients.xdis.view', 'Vis')}
              </Button>
              {sourceExchangeId && (
                <Button type="button" size="sm" onClick={() => setFormFor(row.exchangeId)} data-testid="xdis21-open-version">
                  {t('patients.xdis.new_version', 'Ny version fra denne plan')}
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
      {viewError && <p role="alert" className="text-sm text-ds-red-ink">{viewError}</p>}
      {viewed && (
        <div className="space-y-2">
          <div className="flex justify-end">
            <Button type="button" size="sm" variant="secondary" onClick={() => setViewed(null)}>
              {t('patients.xdis.close', 'Luk')}
            </Button>
          </div>
          <XdisViewer xml={viewed.xml} kind={viewed.kind} patientId={patientId} patientName={patientName} canRevealCpr={canRevealCpr} />
        </div>
      )}
    </section>
  );
}
